

import { Button, Alert } from "@a2z/ui";

import { useEffect, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { StoreSubscriptionPaymentView } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useStoreSubscriptionPaymentsQuery } from "@/modules/stores/services/queries/useStoreSubscriptionPaymentsQuery";
import { SubscriptionPaymentProofModal } from "./SubscriptionPaymentProofModal";

type Props = {
  storeId: string;
  locale: string;
  triggerVariant?: "icon" | "menu";
  triggerClassName?: string;
  triggerLabel?: string;
  onOpen?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

export function StorePaymentsModal({
  storeId,
  locale,
  triggerVariant = "icon",
  triggerClassName,
  triggerLabel,
  onOpen,
  isOpen: controlledOpen,
  onOpenChange,
  hideTrigger,
}: Props) {
  const t = useTranslations();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const {
    data: paymentsData,
    isLoading,
    error: paymentsError,
    refetch,
  } = useStoreSubscriptionPaymentsQuery({
    storeId: isOpen ? storeId : "",
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, setOpen]);

  const formatDate = (value: string | null) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
  };

  const formatAmount = (amount: number | null | undefined, currency?: string) => {
    if (amount === null || amount === undefined) {
      return "-";
    }
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency || "EGP",
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${amount} ${currency ?? ""}`.trim();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      void refetch();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, refetch]);

  const error =
    paymentsError instanceof ApiJsonClientError
      ? paymentsError.message || t(keys.errors.generic)
      : null;
  const payments = (paymentsData ?? []) as StoreSubscriptionPaymentView[];

  const openModal = () => {
    setOpen(true);
    onOpen?.();
  };

  const label = triggerLabel ?? t(keys.admin.storeSubscriptionsPaymentsAction);
  const triggerClasses =
    triggerVariant === "menu"
      ? "w-full text-left text-xs font-semibold text-[color:var(--text-secondary)]"
      : "group relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--border-light)] text-[color:var(--text-muted)] transition hover:border-[color:var(--primary-300)]";

  return (
    <>
      {hideTrigger ? null : (
        <Button variant="ghost"
          type="button"
          onClick={openModal}
          className={`${triggerClasses}${triggerClassName ? ` ${triggerClassName}` : ""}`}
          aria-label={label}
        >
          {triggerVariant === "menu" ? (
            label
          ) : (
            <>
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="white"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="M7 4h7l3 3v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
                  fill="white"
                  stroke="currentColor"
                />
                <path d="M14 4v3h3" fill="white" stroke="currentColor" />
                <path d="M8 12h8" stroke="currentColor" />
                <path d="M8 16h6" stroke="currentColor" />
              </svg>
              <span className="pointer-events-none absolute right-0 top-full z-10 mt-2 whitespace-nowrap rounded-md bg-[color:var(--text-primary)] px-2 py-1 text-[13px] font-semibold text-[color:var(--surface)] opacity-0 shadow-[var(--shadow-sm)] transition-opacity group-hover:opacity-100">
                {label}
              </span>
            </>
          )}
        </Button>
      )}

      {isOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 pt-10 pb-[max(2.5rem,env(safe-area-inset-bottom))]"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
        >
          <div
            className="max-h-full w-full max-w-2xl overflow-y-auto rounded-2xl bg-[color:var(--surface)] p-6 shadow-[var(--shadow-md)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-[color:var(--text-primary)]">
                  {t(keys.admin.subscriptionPaymentsTitle)}
                </h2>
                <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                  {t(keys.admin.subscriptionPaymentsHint)}
                </p>
              </div>
              <Button variant="ghost"
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs font-semibold text-[color:var(--text-muted)]"
              >
                {t(keys.common.close)}
              </Button>
            </div>

            <div className="mt-5">
              {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
              {isLoading ? (
                <div className="flex items-center gap-2 text-xs text-[color:var(--text-muted)]">
                  <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-[color:var(--border-light)] border-t-transparent" />
                  {t(keys.common.loading)}
                </div>
              ) : payments.length === 0 ? (
                <div className="text-xs text-[color:var(--text-muted)]">
                  {t(keys.admin.subscriptionPaymentsEmpty)}
                </div>
              ) : (
                <div className="grid gap-2">
                  {payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="rounded-lg border border-[color:var(--border-light)] px-3 py-2 text-xs"
                    >
                      <div className="flex flex-wrap items-center gap-2 text-[13px] text-[color:var(--text-muted)]">
                        <span>
                          {t(keys.admin.subscriptionPaymentAmountLabel)}:{" "}
                          {formatAmount(payment.amount, payment.currency)}
                        </span>
                        <span>•</span>
                        <span>
                          {t(keys.admin.subscriptionPaymentPaidAt)}:{" "}
                          {formatDate(payment.paidAt)}
                        </span>
                        <span>•</span>
                        <span>
                          {t(keys.admin.subscriptionPaymentNextAt)}:{" "}
                          {formatDate(payment.scheduleDueDate ?? null)}
                        </span>
                        <span>•</span>
                        <span>
                          {t(keys.admin.subscriptionPaymentSettledBy)}:{" "}
                          {payment.settledByAdmin.name}
                        </span>
                      </div>
                      <div className="mt-2">
                        {payment.proofObjectKey ? (
                          <SubscriptionPaymentProofModal
                            storeId={storeId}
                            paymentId={payment.id}
                          />
                        ) : (
                          <div className="text-[13px] text-[color:var(--text-muted)]">
                            {t(keys.admin.subscriptionPaymentProofMissing)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
